import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import MockVSCode from './mock/MockVSCode';
import TypewriterText from './mock/TypewriterText';

const Tabs = [
    { id: 'ide', label: '에디터 (IDE)', icon: 'fas fa-code' },
    { id: 'python', label: '파이썬', icon: 'fa-brands fa-python' },
    { id: 'web', label: '웹 개발', icon: 'fa-brands fa-html5' },
    { id: 'excel', label: '엑셀 VBA', icon: 'fas fa-table' },
    { id: 'c', label: 'C 언어', icon: 'fas fa-copyright' },
];

export default function DevSetupModal({ isOpen, onClose }) {
    const [activeTab, setActiveTab] = useState('ide');

    if (!isOpen) return null;

    return ReactDOM.createPortal(
        <AnimatePresence>
            <motion.div
                key="backdrop"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-md"
                onClick={onClose}
            />

            <motion.div
                key="modal-content"
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="fixed inset-0 z-[10000] flex items-center justify-center p-4 pointer-events-none"
            >
                <div
                    className="w-full max-w-7xl bg-[#0f1219] border border-white/10 rounded-2xl shadow-2xl flex flex-col pointer-events-auto h-[850px] max-h-[95vh] overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-white/5 bg-gradient-to-r from-[#0f1219] to-[#1e293b] shrink-0">
                        <div>
                            <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
                                내 컴퓨터에 '진짜 개발 환경' 만들기 🛠️
                            </h2>
                            <p className="text-slate-400 text-xs mt-0.5">웹 튜터는 이제 그만! 실제 개발자처럼 세팅하고 시작해보세요.</p>
                        </div>
                        <button onClick={onClose} className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-slate-400 hover:text-white transition-colors">
                            <i className="fas fa-times"></i>
                        </button>
                    </div>

                    <div className="flex flex-1 overflow-hidden">
                        {/* Sidebar */}
                        <div className="w-60 bg-black/20 border-r border-white/5 p-3 space-y-1 overflow-y-auto shrink-0 custom-scrollbar">
                            {Tabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all ${activeTab === tab.id
                                        ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 shadow-[0_0_15px_rgba(34,211,238,0.1)]'
                                        : 'text-slate-400 hover:bg-white/5 hover:text-white'
                                        }`}
                                >
                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${activeTab === tab.id ? 'bg-cyan-500/20' : 'bg-white/5'}`}>
                                        <i className={`${tab.icon} ${activeTab === tab.id ? 'text-lg' : 'text-sm'}`}></i>
                                    </div>
                                    <span className="font-bold text-sm tracking-wide">{tab.label}</span>
                                </button>
                            ))}
                        </div>

                        {/* Content */}
                        <div className="flex-1 overflow-hidden bg-[#0f1219] relative">
                            <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none"></div>
                            <div className="relative z-10 w-full h-full p-6">
                                <AnimatePresence mode="wait">
                                    <motion.div
                                        key={activeTab}
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -20 }}
                                        transition={{ duration: 0.2 }}
                                        className="w-full h-full"
                                    >
                                        {activeTab === 'ide' && <IdeContent />}
                                        {activeTab === 'python' && <PythonContent />}
                                        {activeTab === 'web' && <WebContent />}
                                        {activeTab === 'excel' && <ExcelContent />}
                                        {activeTab === 'c' && <CContent />}
                                    </motion.div>
                                </AnimatePresence>
                            </div>
                        </div>
                    </div>
                </div>
            </motion.div>
        </AnimatePresence>,
        document.body
    );
}

// --- Layout System ---

function ContentLayout({ title, accentColor, icon, steps }) {
    const [activeStep, setActiveStep] = useState(0);

    return (
        <div className="h-full flex flex-col">
            <h3 className={`text-2xl font-bold text-white mb-6 flex items-center gap-3 shrink-0`}>
                <div className={`w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center border border-white/10 shadow-lg`}>
                    <i className={`${icon} ${accentColor} text-xl`}></i>
                </div>
                {title}
            </h3>

            <div className="flex flex-col lg:flex-row gap-6 flex-1 min-h-0">
                {/* Steps Column */}
                <div className="w-full lg:w-1/3 flex flex-col gap-3 overflow-y-auto custom-scrollbar pr-2 pb-10">
                    {steps.map((step, index) => (
                        <div
                            key={index}
                            onClick={() => setActiveStep(index)}
                            className={`p-4 rounded-xl border transition-all cursor-pointer relative overflow-hidden group ${activeStep === index
                                ? 'bg-white/10 border-white/20 shadow-lg ring-1 ring-white/10'
                                : 'bg-white/5 border-transparent hover:bg-white/10'
                                }`}
                        >
                            {activeStep === index && (
                                <div className={`absolute left-0 top-0 bottom-0 w-1 ${accentColor.replace('text-', 'bg-')}`}></div>
                            )}

                            <div className="flex items-start gap-4">
                                <span className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold shrink-0 mt-0.5 ${activeStep === index
                                    ? `bg-white text-black`
                                    : 'bg-white/10 text-slate-500'
                                    }`}>
                                    {index + 1}
                                </span>
                                <div className="flex-1">
                                    <h4 className={`font-bold text-sm mb-1 transition-colors ${activeStep === index ? 'text-white' : 'text-slate-400 group-hover:text-slate-200'
                                        }`}>
                                        {step.title}
                                    </h4>
                                    {activeStep === index && (
                                        <motion.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: 'auto' }}
                                            className="space-y-3"
                                        >
                                            <p className="text-xs text-slate-400 leading-relaxed">
                                                {step.desc}
                                            </p>
                                            {/* Extra Tip Box to fill space */}
                                            {step.tip && (
                                                <div className="bg-black/40 border border-white/5 rounded p-3 flex gap-2">
                                                    <i className="fas fa-lightbulb text-yellow-500 text-xs mt-0.5"></i>
                                                    <p className="text-[11px] text-slate-300 leading-normal">
                                                        <span className="font-bold text-yellow-500 mb-1 block">Tip</span>
                                                        {step.tip}
                                                    </p>
                                                </div>
                                            )}
                                        </motion.div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Visualizer Column */}
                <div className="flex-1 bg-[#151921] rounded-2xl border border-white/10 overflow-hidden shadow-2xl relative flex flex-col">
                    <div className="px-5 py-3 border-b border-white/5 bg-[#1a1f2b] flex items-center justify-between shrink-0">
                        <div className="flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-full bg-red-500/80"></span>
                            <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/80"></span>
                            <span className="w-2.5 h-2.5 rounded-full bg-green-500/80"></span>
                        </div>
                        <div className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">
                            Preview : Step {activeStep + 1}
                        </div>
                    </div>

                    <div className="flex-1 relative overflow-hidden bg-[#0f1219]">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={activeStep}
                                initial={{ opacity: 0, scale: 0.98 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.98 }}
                                transition={{ duration: 0.2 }}
                                className="absolute inset-0 p-6 flex flex-col items-center justify-center"
                            >
                                {steps[activeStep].content}
                            </motion.div>
                        </AnimatePresence>
                    </div>
                </div>
            </div>
        </div>
    );
}

// --- Shared Components for Animation ---

function ExtensionInstallAnimation({ searchQuery, extensionItem }) {
    const [processStep, setProcessStep] = useState(0);

    useEffect(() => {
        let mounted = true;
        const sequence = async () => {
            while (mounted) {
                setProcessStep(0); await new Promise(r => setTimeout(r, 1500)); if (!mounted) break;
                setProcessStep(1); await new Promise(r => setTimeout(r, 1000)); if (!mounted) break;
                setProcessStep(2); await new Promise(r => setTimeout(r, 1500)); if (!mounted) break;
                setProcessStep(3); await new Promise(r => setTimeout(r, 1000)); if (!mounted) break;
                setProcessStep(4); await new Promise(r => setTimeout(r, 2000)); if (!mounted) break;
                setProcessStep(5); await new Promise(r => setTimeout(r, 3000));
            }
        };
        sequence();
        return () => { mounted = false; };
    }, []);

    return (
        <div className="w-full h-full relative">
            <MockVSCode
                activeFile="Settings"
                sidebarActive={processStep >= 1 ? "extensions" : "explorer"}
                highlightExtensions={processStep === 0}
                showTerminal={false}
                extensionSearchQuery={
                    processStep >= 2 ? (
                        <TypewriterText text={searchQuery} delay={100} startDelay={0} />
                    ) : ""
                }
                extensionItem={processStep >= 3 ? {
                    ...extensionItem,
                    installed: processStep >= 5
                } : null}
            />

            <AnimatePresence>
                {/* Cursor moving to Extension Tab */}
                {processStep === 0 && (
                    <motion.div
                        initial={{ opacity: 0, x: 20, y: 20 }}
                        animate={{ opacity: 1, x: 0, y: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.5, delay: 0.5 }}
                        className="absolute left-6 top-44 z-50 text-white drop-shadow-xl"
                    >
                        <i className="fas fa-mouse-pointer -rotate-12 text-2xl filter drop-shadow"></i>
                        <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} className="absolute left-5 top-0 bg-pink-600 text-white text-[10px] px-2 py-0.5 rounded font-bold whitespace-nowrap">Click!</motion.span>
                    </motion.div>
                )}

                {/* Simulate Install Click */}
                {processStep === 4 && (
                    <motion.div
                        initial={{ opacity: 0, scale: 1.5 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0 }}
                        className="absolute left-[135px] top-[178px] z-50 w-8 h-8 rounded-full border-2 border-pink-500 bg-pink-500/30 animate-ping"
                    />
                )}

                {/* Restart Toast */}
                {processStep === 5 && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="absolute bottom-4 right-4 bg-blue-600 text-white px-4 py-3 rounded shadow-xl flex flex-col gap-2 z-50 border border-blue-400"
                    >
                        <div className="flex items-center gap-2 text-xs font-bold">
                            <i className="fas fa-info-circle"></i> Restart Required
                        </div>
                        <div className="text-[10px] text-blue-100">
                            설정을 적용하려면 다시 시작하세요.
                        </div>
                        <button className="bg-white/20 hover:bg-white/30 text-xs py-1 rounded transition-colors text-center w-full font-bold">
                            Restart Now
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

// Reuseable Mock MockExcel (Moved out from ExcelContent)
const MockExcel = ({ showDevTab, showVba }) => (
    <div className="w-full max-w-2xl bg-[#1d6f42] rounded-lg overflow-hidden shadow-2xl border border-white/10 flex flex-col h-[400px]">
        <div className="bg-[#103f25] px-4 py-2 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-4">
                <div className="flex gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                </div>
                <span className="text-white text-xs font-bold">통합 문서1 - Excel - {showVba ? 'VBA 모드 (활성화됨)' : '일반 모드'}</span>
            </div>
        </div>
        <div className="bg-[#f3f2f1] border-b border-slate-300 p-1 shrink-0 relative">
            <div className="flex items-center gap-1 mb-1">
                {/* Reduced tabs to minimum to ensure space for left-aligned interaction */}
                {['파일', '홈'].map(m => (
                    <div key={m} className={`px-3 py-1 text-xs text-slate-700 hover:bg-slate-200 rounded cursor-pointer`}>{m}</div>
                ))}
                {showDevTab && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="px-3 py-1 text-xs font-bold rounded cursor-pointer border border-[#1d6f42]/20 text-[#1d6f42]"
                    >
                        개발 도구
                    </motion.div>
                )}
            </div>

            {/* Ribbon Content */}
            <div className="h-20 bg-white border border-slate-200 rounded flex items-center px-4 gap-2">
                {showDevTab ? (
                    <div className="flex flex-col items-center gap-1 group cursor-pointer p-2 bg-slate-100 rounded border border-slate-300">
                        <i className="fas fa-code text-[#1d6f42] text-xl"></i>
                        <span className="text-[10px] text-slate-800 font-bold">Visual Basic</span>
                    </div>
                ) : (
                    <div className="flex items-center gap-4 opacity-50 grayscale">
                        <div className="w-8 h-8 bg-slate-200 rounded"></div>
                        <div className="w-8 h-8 bg-slate-200 rounded"></div>
                        <div className="w-8 h-8 bg-slate-200 rounded"></div>
                    </div>
                )}
            </div>
        </div>

        <div className="flex-1 bg-white relative">
            {showVba ? (
                <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="absolute inset-4 bg-[#f0f0f0] border border-slate-400 shadow-xl rounded-sm flex flex-col z-20">
                    <div className="bg-[#e0e0e0] px-2 py-1 text-[10px] text-black border-b border-slate-300 flex justify-between select-none">
                        <span>Microsoft Visual Basic for Applications - 통합 문서1</span>
                        <span className="cursor-pointer hover:bg-red-500 hover:text-white px-1">X</span>
                    </div>
                    <div className="flex-1 flex">
                        <div className="w-40 bg-white border-r border-slate-300 p-2">
                            <div className="text-[10px] text-slate-600 font-bold mb-1">프로젝트 - VBAProject</div>
                            <div className="pl-2 text-[10px] text-slate-500">
                                <div className="flex items-center gap-1"><i className="far fa-folder text-yellow-500"></i> MS Excel 개체</div>
                                <div className="pl-4 flex items-center gap-1 bg-blue-100"><i className="far fa-file"></i> Sheet1</div>
                                <div className="pl-4 flex items-center gap-1"><i className="far fa-file"></i> 현재_통합_문서</div>
                            </div>
                        </div>
                        <div className="flex-1 bg-white font-mono text-[11px] p-2 text-blue-900 selection:bg-blue-200">
                            <div>Sub SystemHello()</div>
                            <div className="pl-4 text-black">
                                <TypewriterText text='MsgBox "엑셀이 이상하게 동작합니다..."' delay={30} startDelay={500} />
                            </div>
                            <div>End Sub</div>
                        </div>
                    </div>
                </motion.div>
            ) : (
                <div className="grid grid-cols-[30px_1fr_1fr_1fr] h-full text-xs text-slate-500">
                    <div className="bg-slate-100 border-r border-b border-slate-300"></div>
                    <div className="bg-slate-100 border-r border-b border-slate-300 flex items-center justify-center font-bold">A</div>
                    <div className="bg-slate-100 border-r border-b border-slate-300 flex items-center justify-center font-bold">B</div>
                    <div className="bg-slate-100 border-r border-b border-slate-300 flex items-center justify-center font-bold">C</div>
                    {[1, 2, 3, 4, 5].map(r => (
                        <React.Fragment key={r}>
                            <div className="bg-slate-100 border-r border-b border-slate-300 flex items-center justify-center font-bold">{r}</div>
                            <div className="border-r border-b border-slate-200"></div>
                            <div className="border-r border-b border-slate-200"></div>
                            <div className="border-r border-b border-slate-200"></div>
                        </React.Fragment>
                    ))}
                </div>
            )}
        </div>
    </div>
);

// New Animation Component for Excel
function ExcelSetupAnimation() {
    // 0:Idle, 1:RightClick, 2:MenuOpen, 3:ClickCustomize, 4:OptionsOpen, 5:HoverCheck, 6:ClickCheck, 7:ClickOK, 8:Done
    const [step, setStep] = useState(0);

    useEffect(() => {
        let mounted = true;
        const seq = async () => {
            while (mounted) {
                setStep(0); await new Promise(r => setTimeout(r, 1000)); if (!mounted) break;
                setStep(1); await new Promise(r => setTimeout(r, 800)); if (!mounted) break; // Move & RightClick
                setStep(2); await new Promise(r => setTimeout(r, 800)); if (!mounted) break; // Menu Shows
                setStep(3); await new Promise(r => setTimeout(r, 800)); if (!mounted) break; // Move to Customize
                setStep(4); await new Promise(r => setTimeout(r, 1200)); if (!mounted) break; // Click Customize -> Options Open
                setStep(5); await new Promise(r => setTimeout(r, 800)); if (!mounted) break; // Move to Checkbox
                setStep(6); await new Promise(r => setTimeout(r, 600)); if (!mounted) break; // Click Checkbox
                setStep(7); await new Promise(r => setTimeout(r, 800)); if (!mounted) break; // Move to OK & Click
                setStep(8); await new Promise(r => setTimeout(r, 3000)); // Show Result
            }
        };
        seq();
        return () => { mounted = false; };
    }, []);

    // Cursor Coordinate Calculation
    const getCursorPos = (s) => {
        // Modal is fixed at left-[80px] w-[420px]
        // Coordinates relative to main container:
        // Checkbox calculated approx: 80(left) + 274(inside) = ~354. Lets use 360.
        // OK Button calculated approx: 80(left) + 314(inside) = ~394. Lets use 395.
        // Y adjustments based on feedback: 
        // Checkbox: 195 was 'Add-ins' (too low). Needs to go UP to 'Developer'. -> 170.
        // OK Button: 310 was 'Slightly above'. Needs to go DOWN. -> 330.

        switch (s) {
            case 0: return { x: 130, y: 150, opacity: 0 };
            case 1: return { x: 130, y: 45, opacity: 1 }; // Right click spot
            case 2: return { x: 130, y: 45, opacity: 1 }; // Menu Open, stay
            case 3: return { x: 200, y: 80, opacity: 1 }; // "Customize..." center
            case 4: return { x: 200, y: 80, opacity: 1 }; // Clicked, wait for modal
            case 5: return { x: 360, y: 170, opacity: 1 }; // Hover Checkbox (Adjusted Up)
            case 6: return { x: 360, y: 170, opacity: 1 }; // Click Checkbox (Adjusted Up)
            case 7: return { x: 395, y: 330, opacity: 1 }; // Hover OK Button (Adjusted Down)
            default: return { x: 395, y: 330, opacity: 0 }; // Done
        }
    };

    const cursorPos = getCursorPos(step);

    return (
        <div className="w-full h-full flex flex-col items-center justify-center">
            <div className="relative w-full max-w-2xl">
                <MockExcel showDevTab={step >= 8} showVba={false} />

                <AnimatePresence>
                    {/* Unified Cursor */}
                    {step < 8 && (
                        <motion.div
                            animate={{
                                x: cursorPos.x,
                                y: cursorPos.y,
                                opacity: cursorPos.opacity
                            }}
                            transition={{ duration: 0.5, ease: "easeInOut" }}
                            className="absolute left-0 top-0 z-50 pointer-events-none"
                        >
                            <i className="fas fa-mouse-pointer text-white text-2xl drop-shadow-lg -rotate-12"></i>

                            {/* Click Effects */}
                            {(step === 1 || step === 3 || step === 6 || step === 7) && (
                                <motion.div
                                    key={step} // Re-trigger animation on step change
                                    initial={{ scale: 0, opacity: 1 }}
                                    animate={{ scale: 1.5, opacity: 0 }}
                                    transition={{ duration: 0.5 }}
                                    className={`absolute -left-2 -top-2 w-8 h-8 border-2 rounded-full ${step === 1 ? 'border-yellow-400' : 'bg-pink-500/50 border-transparent'}`}
                                />
                            )}
                        </motion.div>
                    )}

                    {/* Context Menu */}
                    {step >= 2 && step < 4 && (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0 }}
                            className="absolute left-[130px] top-[45px] bg-white text-black text-[10px] shadow-xl border border-slate-300 py-1 rounded w-48 z-40 origin-top-left"
                            style={{ minWidth: "200px" }}
                        >
                            <div className="px-3 py-1.5 hover:bg-slate-100">리본 메뉴 축소</div>
                            <div className={`px-3 py-1.5 font-bold ${step === 3 ? 'bg-blue-100 text-blue-800 border-l-2 border-blue-500' : 'text-slate-700'}`}>
                                리본 메뉴 사용자 지정...
                            </div>
                            <div className="px-3 py-1.5 hover:bg-slate-100">빠른 실행 도구 모음 표시</div>
                        </motion.div>
                    )}

                    {/* Options Modal */}
                    {step >= 4 && step < 8 && (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            // Fixed left positioning to prevent overflow (removed left-1/2 centering)
                            className="absolute top-10 bottom-10 left-[80px] w-[420px] bg-white rounded-lg shadow-2xl border border-slate-300 z-40 flex flex-col font-sans text-xs"
                        >
                            <div className="p-3 border-b text-sm font-bold text-slate-700">Excel 옵션</div>
                            <div className="flex-1 flex overflow-hidden">
                                <div className="w-1/4 bg-slate-100 p-2 space-y-1 text-slate-600 border-r">
                                    <div>일반</div>
                                    <div>수식</div>
                                    <div className="bg-blue-100 text-blue-700 font-bold px-2 py-0.5 rounded">리본 사용자 지정</div>
                                </div>
                                <div className="flex-1 p-3">
                                    <div className="flex gap-2 h-full">
                                        <div className="flex-1 border p-2 bg-white text-slate-400">
                                            (많이 사용하는 명령...)
                                        </div>
                                        <div className="flex-1 border p-2 bg-white flex flex-col gap-1">
                                            <div className="font-bold mb-1">기본 탭</div>
                                            <div className="flex items-center gap-1"><input type="checkbox" checked readOnly className="accent-green-600" /> 홈</div>
                                            <div className="flex items-center gap-1"><input type="checkbox" checked readOnly className="accent-green-600" /> 삽입</div>
                                            <div className="flex items-center gap-1">
                                                <input type="checkbox" checked={step >= 6} readOnly className="accent-green-600 scale-125" />
                                                <span className={step >= 6 ? "font-bold text-black" : "text-slate-500"}>개발 도구</span>
                                            </div>
                                            <div className="flex items-center gap-1"><input type="checkbox" checked readOnly className="accent-green-600" /> 추가 기능</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="p-2 border-t flex justify-end gap-2 bg-slate-50">
                                <button className={`px-4 py-1.5 rounded border ${step === 7 ? 'bg-blue-600 text-white shadow-inner' : 'bg-white text-slate-700'}`}>확인</button>
                                <button className="px-4 py-1.5 rounded border bg-white text-slate-700">취소</button>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}

// --- Content Components ---

function IdeContent() {
    const [tourStage, setTourStage] = useState(0);

    useEffect(() => {
        const timer = setInterval(() => {
            setTourStage(prev => (prev + 1) % 4);
        }, 2500);
        return () => clearInterval(timer);
    }, []);

    const steps = [
        {
            title: "나에게 맞는 에디터 찾기",
            desc: "개발 스타일과 AI 활용도에 맞춰 나만의 무기를 선택하세요. 안정적인 표준 에디터와 혁신적인 AI 에디터 중 하나를 고를 수 있습니다.",
            tip: "VS Code는 전 세계 표준이며, Antigravity는 AI 에이전트와 함께하는 미래형 경험을 제공합니다.",
            content: (
                <div className="grid grid-cols-2 gap-4 w-full h-full max-h-[500px]">
                    {/* VS Code Card */}
                    <div className="group relative p-5 bg-[#1e293b]/30 border border-white/10 rounded-xl hover:border-blue-500/50 hover:bg-[#1e293b]/50 transition-all duration-300 flex flex-col gap-3">
                        <div className="absolute -top-3 left-4 px-2 py-0.5 bg-blue-600 rounded text-[10px] font-bold text-white uppercase shadow-lg z-10">Standard</div>

                        <div className="flex items-center gap-3 mb-2">
                            <img src="https://upload.wikimedia.org/wikipedia/commons/9/9a/Visual_Studio_Code_1.35_icon.svg" alt="VS Code" className="w-10 h-10 drop-shadow-xl" />
                            <div>
                                <h4 className="text-lg font-bold text-white leading-tight">Visual Studio Code</h4>
                                <div className="text-[10px] text-slate-400 font-bold">$10/월 (Copilot 별도)</div>
                            </div>
                        </div>

                        {/* Feature List */}
                        <div className="flex-1 space-y-2">
                            <div className="flex items-center gap-2 text-xs text-slate-300">
                                <i className="fas fa-check-circle text-blue-500"></i>
                                <span>압도적인 확장 프로그램 생태계</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-slate-300">
                                <i className="fas fa-check-circle text-blue-500"></i>
                                <span>안정적인 로컬 개발 환경</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-slate-300">
                                <i className="fas fa-check-circle text-blue-500"></i>
                                <span>Git 버전 관리 기본 탑재</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-slate-300">
                                <i className="fas fa-check-circle text-blue-500"></i>
                                <span>초기 비용 0원 (완전 무료)</span>
                            </div>
                        </div>

                        <div className="flex items-center gap-2 mt-2">
                            <span className="px-2 py-1 bg-white/5 rounded text-[10px] text-slate-400 border border-white/5">오프라인 최적</span>
                            <span className="px-2 py-1 bg-white/5 rounded text-[10px] text-slate-400 border border-white/5">업계 표준</span>
                        </div>

                        <a href="https://code.visualstudio.com/" target="_blank" rel="noopener noreferrer" className="mt-auto block w-full py-2.5 bg-blue-600 hover:bg-blue-500 text-white text-xs rounded-lg font-bold text-center transition-colors">
                            다운로드 (공식)
                        </a>
                    </div>

                    {/* Antigravity Card */}
                    <div className="group relative p-5 bg-gradient-to-br from-[#1e293b]/50 to-purple-900/20 border border-purple-500/30 rounded-xl hover:border-purple-400 hover:shadow-[0_0_20px_rgba(168,85,247,0.2)] transition-all duration-300 flex flex-col gap-3">
                        <div className="absolute -top-3 left-4 px-2 py-0.5 bg-purple-600 rounded text-[10px] font-bold text-white uppercase shadow-lg flex items-center gap-1 z-10">
                            <i className="fas fa-crown text-yellow-300 text-[8px]"></i> Premium Value
                        </div>

                        <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center border border-white/10 shadow-inner">
                                <i className="fas fa-brain text-2xl text-purple-400"></i>
                            </div>
                            <div>
                                <h4 className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-white to-purple-200 leading-tight">Antigravity</h4>
                                <div className="text-[10px] text-purple-300 font-bold">$20/월 (Google AI 통합)</div>
                            </div>
                        </div>

                        {/* Feature List */}
                        <div className="flex-1 space-y-2">
                            <div className="flex items-center gap-2 text-xs text-slate-200">
                                <i className="fas fa-robot text-purple-400 animate-pulse"></i>
                                <span className="font-bold text-purple-200">에이전트 중심 (자율 코딩)</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-slate-300">
                                <i className="fas fa-check text-purple-500"></i>
                                <span>VS Code 기반 (100% 호환)</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-slate-300">
                                <i className="fas fa-check text-purple-500"></i>
                                <span>Gemini 3.0 네이티브 탑재</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-slate-300">
                                <i className="fas fa-check text-purple-500"></i>
                                <span>UI 테스트용 브라우저 내장</span>
                            </div>
                        </div>

                        <div className="flex items-center gap-2 mt-2">
                            <span className="px-2 py-1 bg-purple-500/10 rounded text-[10px] text-purple-300 border border-purple-500/20">에이전트 네이티브</span>
                            <span className="px-2 py-1 bg-purple-500/10 rounded text-[10px] text-purple-300 border border-purple-500/20">구글 내부 기술</span>
                        </div>

                        <a href="https://antigravity.google/" target="_blank" rel="noopener noreferrer" className="mt-auto block w-full py-2.5 bg-purple-600 hover:bg-purple-500 text-white text-xs rounded-lg font-bold text-center transition-colors shadow-lg shadow-purple-900/30">
                            Antigravity 다운로드
                        </a>
                    </div>
                </div>
            )
        },
        {
            title: "인터페이스 투어 (Tour)",
            desc: "VS Code는 크게 3가지 영역으로 나뉩니다. 탐색기, 에디터, 그리고 터미널입니다.",
            tip: "각 영역의 역할을 눈으로 익혀두세요. 잠시 후 직접 사용하게 됩니다.",
            content: (
                <div className="w-full h-full relative">
                    <MockVSCode
                        activeFile="main.py"
                        sidebarActive="explorer"
                        showTerminal={true}
                        editorContent={<div className="text-slate-500">// 코드를 작성하는 공간입니다.</div>}
                        terminalContent={<div className="text-slate-500"># 명령어를 입력하고 결과를 확인합니다.</div>}
                        highlightExtensions={tourStage === 1}
                        highlightSidebar={tourStage === 1}
                        highlightTerminal={tourStage === 3}
                    />
                    {/* Floating Labels */}
                    <AnimatePresence>
                        {tourStage === 1 && (
                            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }} className="absolute left-16 top-20 bg-blue-600 text-white px-3 py-1 rounded text-xs font-bold arrow-left z-50">
                                1. 사이드바 (탐색기/확장)
                            </motion.div>
                        )}
                        {tourStage === 2 && (
                            <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="absolute top-1/3 left-1/2 -translate-x-1/2 bg-yellow-600 text-white px-3 py-1 rounded text-xs font-bold arrow-down z-50">
                                2. 코드 에디터 (Main)
                            </motion.div>
                        )}
                        {tourStage === 3 && (
                            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="absolute bottom-20 left-1/2 -translate-x-1/2 bg-indigo-600 text-white px-3 py-1 rounded text-xs font-bold arrow-up z-50">
                                3. 터미널 (Output)
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            )
        },
        {
            title: "필수 설정 : 한글 패치",
            desc: "영어가 낯설다면 '확장(Extensions)' 탭에서 한글 팩을 설치하세요.",
            tip: "설치 후 우측 하단에 뜨는 'Restart' 버튼을 꼭 눌러야 적용됩니다.",
            content: (
                <ExtensionInstallAnimation
                    searchQuery="Korean"
                    extensionItem={{
                        name: "Korean Language Pack",
                        description: "Korean Language Pack for VS Code",
                        icon: <span className="text-blue-400 font-bold text-lg">가</span>
                    }}
                />
            )
        }
    ];
    return <ContentLayout title="통합 개발 환경 (IDE)" accentColor="text-blue-400" icon="fas fa-code" steps={steps} />;
}

function PythonContent() {
    const steps = [
        {
            title: "1. 엔진 확보 (Python 설치)",
            desc: "자동차에 엔진이 필요하듯, 파이썬 코드를 돌리려면 Python 인터프리터가 필요합니다.",
            tip: "'Add python.exe to PATH' 체크박스를 놓치면 나중에 고생합니다. 꼭 체크하세요!",
            content: (
                <div className="w-full max-w-lg space-y-6">
                    <div className="bg-[#1e1e1e] p-8 rounded-xl border border-white/10 shadow-xl flex flex-col items-center text-center relative overflow-hidden">
                        <div className="absolute inset-0 bg-yellow-500/5 pointer-events-none"></div>
                        <i className="fab fa-python text-7xl text-yellow-400 mb-6 drop-shadow-lg"></i>
                        <h4 className="text-2xl font-bold text-white mb-2">Python 3.12.1</h4>

                        <div className="w-full bg-[#2d2d2d] p-4 rounded-lg border-2 border-red-500 flex items-center gap-4 mb-6 shadow-[0_0_20px_rgba(239,68,68,0.2)] animate-pulse">
                            <div className="w-6 h-6 bg-blue-500 border border-white flex items-center justify-center rounded text-white text-xs"><i className="fas fa-check"></i></div>
                            <span className="text-base text-white font-bold">Add python.exe to PATH</span>
                        </div>
                        <a href="https://www.python.org/downloads/" target="_blank" rel="noopener noreferrer" className="px-8 py-3 bg-yellow-500 hover:bg-yellow-400 text-black font-bold rounded-lg transition-colors text-sm w-full shadow-lg shadow-yellow-500/20">
                            Download from Python.org
                        </a>
                    </div>
                </div>
            )
        },
        {
            title: "2. 무기 장착 (VS Code 확장)",
            desc: "VS Code가 파이썬 문법을 이해하고 자동완성을 해줄 수 있도록 전용 확장을 설치합니다.",
            tip: "마이크로소프트(Microsoft) 인증 마크가 있는 공식 확장을 설치하세요.",
            content: (
                <ExtensionInstallAnimation
                    searchQuery="Python"
                    extensionItem={{
                        name: "Python",
                        description: "IntelliSense, linting, debugging for Python.",
                        icon: <i className="fab fa-python text-blue-400 text-xl"></i>
                    }}
                />
            )
        },
        {
            title: "3. 첫 코드 실행 (Hello World)",
            desc: "모든 것이 준비되었습니다. 첫 번째 파이썬 프로그램을 작성하고 실행해봅시다.",
            tip: "재생 버튼(▶)을 누르면 하단 터미널에 결과가 출력됩니다.",
            content: (
                <div className="w-full h-full relative">
                    <MockVSCode
                        activeFile="hello.py"
                        highlightPlayButton={true}
                        editorContent={
                            <div className="text-green-400">
                                <TypewriterText text='print("Hello World!")' delay={50} startDelay={500} />
                            </div>
                        }
                        showTerminal={true}
                        showCursorLine={false}
                        terminalContent={
                            <div className="mt-2">
                                <div className="text-slate-400">
                                    D:\my_project{'>'} <TypewriterText text="python hello.py" delay={30} startDelay={2000} />
                                </div>
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: 3.5 }}
                                    className="text-white mt-1 font-bold"
                                >
                                    Hello World!
                                </motion.div>
                                {/* 실행 완료 후 커서 표시 */}
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: 4 }}
                                    className="flex items-center gap-1 mt-1"
                                >
                                    <span className="text-green-400">D:\my_project{'>'}</span>
                                    <span className="animate-pulse bg-slate-500 w-2 h-4 block"></span>
                                </motion.div>
                            </div>
                        }
                    />
                </div>
            )
        }
    ];
    return <ContentLayout title="파이썬 (Python)" accentColor="text-yellow-400" icon="fa-brands fa-python" steps={steps} />;
}

function WebContent() {
    const steps = [
        {
            title: "1. 관측기 확보 (Chrome)",
            desc: "웹 브라우저는 여러분이 만든 사이트를 보여주는 창문입니다. 크롬이 개발자에게 가장 친화적입니다.",
            tip: "이미 크롬을 쓰고 계시다면 1단계는 완료입니다!",
            content: (
                <div className="flex flex-col items-center justify-center gap-8">
                    <div className="relative group">
                        <div className="absolute inset-0 bg-blue-500/20 blur-2xl rounded-full group-hover:bg-blue-500/30 transition-all"></div>
                        <img src="https://upload.wikimedia.org/wikipedia/commons/e/e1/Google_Chrome_icon_%28February_2022%29.svg" alt="Chrome" className="w-40 h-40 relative z-10 drop-shadow-2xl hover:scale-105 transition-transform duration-300" />
                    </div>
                </div>
            )
        },
        {
            title: "2. 엔진 장착 (Live Server)",
            desc: "파일을 저장할 때마다 자동으로 브라우저를 새로고침 해주는 필수 확장 프로그램입니다.",
            tip: "이게 없으면 코드 한 줄 고칠 때마다 F5를 눌러야 합니다. 꼭 설치하세요.",
            content: (
                <ExtensionInstallAnimation
                    searchQuery="Live Server"
                    extensionItem={{
                        name: "Live Server",
                        description: "Launch development local Server with live reload.",
                        icon: <i className="fas fa-broadcast-tower text-purple-400 text-xl"></i>
                    }}
                />
            )
        },
        {
            title: "3. 방송 시작 (Go Live)",
            desc: "HTML 파일을 열고 우측 하단의 'Go Live' 버튼을 클릭하면 내 컴퓨터가 서버가 됩니다.",
            tip: "서버가 켜지면 브라우저가 자동으로 열리며 페이지가 뜹니다.",
            content: (
                <div className="w-full h-full relative">
                    <MockVSCode
                        activeFile="index.html"
                        editorContent={
                            <div className="text-xs font-mono">
                                <div className="text-blue-400">{'<!DOCTYPE html>'}</div>
                                <div>{'<html>'}</div>
                                <div className="pl-4">{'<body>'}</div>
                                <div className="pl-8 text-white">
                                    <TypewriterText text="<h1>Hello World!</h1>" delay={50} startDelay={500} />
                                </div>
                                <div className="pl-4">{'</body>'}</div>
                                <div>{'</html>'}</div>
                            </div>
                        }
                        showTerminal={false}
                    />
                    <div className="absolute bottom-2 right-4 z-50">
                        <motion.button
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ delay: 2, type: 'spring' }}
                            className="bg-[#252526] text-white px-3 py-1 rounded-sm text-[10px] bg-blue-600 hover:bg-blue-500 flex items-center gap-2 ring-2 ring-blue-500/50 shadow-lg"
                        >
                            <i className="fas fa-broadcast-tower animate-pulse"></i> Go Live
                        </motion.button>
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 2.5 }}
                            className="absolute bottom-full right-0 mb-2 whitespace-nowrap bg-white text-black text-xs font-bold px-2 py-1 rounded"
                        >
                            Click Me! ▼
                        </motion.div>
                    </div>
                </div>
            )
        }
    ];
    return <ContentLayout title="웹 개발 (Web)" accentColor="text-orange-500" icon="fa-brands fa-html5" steps={steps} />;
}

function ExcelContent() {
    const steps = [
        {
            title: "1. 개발 도구 탭 켜기",
            desc: "엑셀 상단 메뉴 빈 공간을 우클릭하여 '리본 메뉴 사용자 지정'을 선택하거나, 옵션 창에서 체크하세요.",
            tip: "숨겨진 '개발자 모드'를 해금하는 과정입니다. 마우스 우클릭으로 빠르게 진입할 수 있습니다.",
            content: (
                <ExcelSetupAnimation />
            )
        },
        {
            title: "2. VBA 에디터 열기 (Alt+F11)",
            desc: "이제 '개발 도구' 탭이 보입니다. 'Visual Basic' 아이콘을 누르거나, 단축키 Alt + F11로 에디터를 엽니다.",
            tip: "마우스보다 단축키(Alt + F11)를 쓰는 것이 훨씬 빠르고 '프로' 같습니다.",
            content: (
                <div className="flex flex-col items-center justify-center gap-4 w-full h-full">
                    <MockExcel showDevTab={true} showVba={true} />
                </div>
            )
        },
        {
            title: "3. 파일 저장 (.xlsm)",
            desc: "일반 엑셀 파일(.xlsx)로 저장하면 방금 짠 코드가 모두 사라집니다! 정말 많이 하는 실수입니다.",
            tip: "반드시 '매크로 사용 통합 문서 (.xlsm)' 형식을 선택해서 저장하세요.",
            content: (
                <div className="text-center p-8 bg-[#1e293b] rounded-xl border border-red-500/30 shadow-xl w-full max-w-sm">
                    <div className="flex justify-center mb-6 text-6xl gap-4">
                        <i className="fas fa-file-excel text-[#1d6f42]"></i>
                        <i className="fas fa-arrow-right text-slate-600 text-3xl mt-4"></i>
                        <i className="fas fa-file-code text-green-400"></i>
                    </div>
                    <div className="bg-white text-black px-4 py-3 rounded flex items-center justify-between w-full mx-auto border-2 border-blue-500 ring-4 ring-blue-500/20 mb-4">
                        <span className="text-sm font-bold">Project_Final.xlsm</span>
                        <i className="fas fa-chevron-down text-slate-400"></i>
                    </div>
                    <p className="text-red-400 text-sm font-bold animate-pulse bg-red-900/20 py-2 rounded">
                        ⚠️ .xlsx 저장 시 코드 삭제됨
                    </p>
                </div>
            )
        }
    ];

    return <ContentLayout title="엑셀 VBA (Excel)" accentColor="text-green-500" icon="fas fa-table" steps={steps} />;
}

function CContent() {
    const steps = [
        {
            title: "1. 환경 선택 (Replit 추천)",
            desc: "C언어는 설치가 까다롭습니다. 초보자라면 설치 없이 바로 시작할 수 있는 Replit(웹 에디터)을 강력 추천합니다.",
            tip: "VS Code(Local) 세팅은 컴파일러 설치 등 복잡한 과정이 필요해, 중급자 이상에게 권장합니다.",
            content: (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full h-full p-4 items-center">
                    <div className="bg-[#1e293b] rounded-xl border-2 border-cyan-500 p-6 flex flex-col hover:bg-[#1e293b]/80 transition-all shadow-[0_0_20px_rgba(6,182,212,0.2)] scale-105 z-10">
                        <div className="flex items-center justify-between mb-4">
                            <i className="fas fa-cloud text-4xl text-cyan-400"></i>
                            <span className="px-2 py-1 bg-cyan-500 text-black text-[10px] font-bold rounded uppercase">Best for Beginner</span>
                        </div>
                        <h4 className="text-xl font-bold text-white mb-2">Replit (Web)</h4>
                        <p className="text-sm text-slate-400 mb-6">설치 0초. 인터넷만 되면 어디서든 코딩 가능.</p>
                        <button className="py-3 bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-bold rounded-lg text-center transition-colors">
                            Replit 시작하기
                        </button>
                    </div>

                    <div className="bg-[#1e293b]/50 rounded-xl border border-white/10 p-6 flex flex-col hover:border-white/30 transition-all opacity-70 hover:opacity-100">
                        <div className="flex items-center justify-between mb-4">
                            <i className="fas fa-desktop text-4xl text-slate-500"></i>
                            <span className="px-2 py-1 bg-slate-700 text-slate-400 text-[10px] font-bold rounded uppercase">Advanced</span>
                        </div>
                        <h4 className="text-xl font-bold text-white mb-2">Local (VS Code)</h4>
                        <p className="text-sm text-slate-400 mb-6">MinGW 설치, 환경변수 설정 등 복잡한 준비 필요.</p>
                        <button disabled className="py-3 bg-white/5 text-slate-500 text-sm font-bold rounded-lg text-center cursor-not-allowed border border-white/5">
                            별도 가이드 필요
                        </button>
                    </div>
                </div>
            )
        },
        {
            title: "2. Replit 사용법",
            desc: "로그인 후 '+ Create Repl' 버튼을 누르고, 언어로 'C'를 선택하면 모든 준비가 끝납니다.",
            tip: "Template에서 꼭 'C'를 선택해야 합니다. (C++이나 C# 아님!)",
            content: (
                <div className="w-full h-full bg-white rounded-lg overflow-hidden flex flex-col shadow-2xl">
                    <div className="bg-slate-800 px-3 py-2 flex items-center gap-2 shrink-0">
                        <div className="w-2 h-2 rounded-full bg-red-400"></div>
                        <span className="text-xs text-slate-300">replit.com</span>
                    </div>
                    <div className="flex-1 bg-[#1c2333] p-6 flex gap-6 overflow-hidden relative">
                        {/* Sidebar Mock */}
                        <div className="w-48 space-y-4 shrink-0">
                            <div className="h-10 bg-blue-600 rounded-lg flex items-center justify-center text-white text-sm font-bold shadow-lg shadow-blue-900/50 cursor-pointer hover:bg-blue-500 transition-colors">
                                <i className="fas fa-plus mr-2"></i> Create Repl
                            </div>
                            <div className="space-y-2 opacity-50">
                                <div className="h-4 bg-slate-700 rounded w-full"></div>
                                <div className="h-4 bg-slate-700 rounded w-3/4"></div>
                                <div className="h-4 bg-slate-700 rounded w-5/6"></div>
                            </div>
                        </div>

                        {/* Modal Mock */}
                        <div className="flex-1 bg-[#2b3245] rounded-lg border border-slate-600 p-6 shadow-2xl relative z-10">
                            <div className="text-lg font-bold text-white mb-6">Create a Repl</div>
                            <div className="space-y-4">
                                <div>
                                    <label className="text-xs text-slate-400 font-bold uppercase mb-1 block">Template</label>
                                    <div className="bg-[#1c2333] border border-blue-500 p-3 rounded flex items-center justify-between text-white text-sm">
                                        <div className="flex items-center gap-2">
                                            <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center text-[10px] font-bold">C</div>
                                            <span>C</span>
                                        </div>
                                        <i className="fas fa-check text-blue-500"></i>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-xs text-slate-400 font-bold uppercase mb-1 block">Title</label>
                                    <div className="bg-[#1c2333] border border-slate-600 p-3 rounded text-slate-500 text-sm">
                                        Amazing C Project
                                    </div>
                                </div>
                            </div>
                            <button className="mt-6 w-full py-3 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-lg">+ Create Repl</button>
                        </div>
                    </div>
                </div>
            )
        }
    ];

    return <ContentLayout title="C 언어 (C Lang)" accentColor="text-blue-500" icon="fas fa-copyright" steps={steps} />;
}
